import { ApolloQueryResult } from '@apollo/client';
import React, { useCallback } from 'react';
import type { Spacing } from '@atlaskit/button';
import Button from '@atlaskit/button/standard-button';
import { CopyableText, SummarySectionV2, useSidebar, useFeatureToggle } from '@css-xp/base';
import {
  BillingFrequency,
  EntitlementDetails,
  LinkedDestinationEntitlement,
} from '@css-xp/graphtypes/src/generated-types-new';
import { UFOUiAction, ufoHandleButtonClick } from '@atlassian/temp-observability';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { SIDEBAR } from '../../../../entitlement-page/src/components/entitlement-page';
import { AnalyticsUiAction, AnalyticsUiSubject, analytics, AnalyticsTrackSubject } from '@atlassian/temp-analytics';

export const ViewPricingDetails = ({
  entitlement,
  linkedEntitlements,
  refetch,
  transactionId,
  invoiceRedirectPath,
  buttonSpacing,
  hidePricingCalculatorText = false,
  buttonAppearance = 'link',
}: {
  entitlement: EntitlementDetails;
  transactionId?: string;
  linkedEntitlements: LinkedDestinationEntitlement[];
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  invoiceRedirectPath?: string;
  buttonSpacing?: Spacing;
  hidePricingCalculatorText?: boolean;
  buttonAppearance?: 'link' | 'default';
}) => {
  const { openSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);

  const { isApp, logoId } = entitlement;
  let activeUsers = 0;
  let sortedParentEntitlements: LinkedDestinationEntitlement[] = [];
  if (entitlement?.billingFrequency === BillingFrequency.ANNUAL && !isStatuspage(entitlement.productId)) {
    activeUsers = entitlement.userTier as number;
  } else if (isApp && linkedEntitlements.length > 0 && !isStatuspage(entitlement.productId)) {
    sortedParentEntitlements = linkedEntitlements.sort(
      (a, b) => (Number(b?.activeUsers) ?? 0) - (Number(a?.activeUsers) ?? 0)
    );
    activeUsers = sortedParentEntitlements[0]?.activeUsers ?? 0;
  } else {
    activeUsers = entitlement?.activeUsers ?? 0;
  }

  return (
    <>
      {entitlement?.pricingPlan?.type && (
        <div className="entitlement-pricing-details">
          <Button
            aria-label="View pricing details"
            appearance={buttonAppearance}
            className="inner-text"
            spacing={buttonSpacing}
            onClick={() => {
              ufoHandleButtonClick(UFOUiAction.GET_PRICING_DETAILS);
              analytics.sendUIEvent({
                action: AnalyticsUiAction.CLICKED,
                actionSubject: AnalyticsUiSubject.BUTTON,
                actionSubjectId: AnalyticsTrackSubject.PRICING,
              });
              openSidebar({
                sidebarType: SIDEBAR.VIEW_PRICING_DETAILS,
                payload: {
                  entitlementId: entitlement?.id,
                  transactionId: transactionId,
                  entitlementNumber: entitlement?.entitlementNumber,
                  productId: entitlement?.productId,
                  productName: entitlement?.productName,
                  offeringName: entitlement?.offering?.name,
                  billingFrequency: entitlement?.billingFrequency,
                  pricingPlan: entitlement?.pricingPlan,
                  activeUsers,
                  isApp,
                  logoId,
                  refetch,
                  invoiceRedirectPath,
                  hidePricingCalculatorText,
                  version: isDesignIteration1Enabled ? 2 : undefined,
                },
                sidebarOptions: {
                  isOverlay: true,
                  sidebarWidth: isDesignIteration1Enabled ? '60%' : '80%',
                },
              });
            }}
          >
            View
          </Button>
        </div>
      )}
    </>
  );
};

export const PricingSection = ({
  entitlement,
  linkedEntitlements,
  refetch,
  isViewPricingEnabled = true,
}: {
  entitlement: EntitlementDetails;
  linkedEntitlements: LinkedDestinationEntitlement[];
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  isViewPricingEnabled?: boolean;
}) => {
  const getPricingPlan = useCallback(() => {
    const { pricingPlan } = entitlement;
    return (
      <div className="entitlement-details-column">
        {pricingPlan?.type ? (
          <CopyableText>
            <span>{sentenceCase(pricingPlan?.type)}</span>
          </CopyableText>
        ) : (
          <span data-testid="empty-pricing-plan" className="data-unavailable">
            N/A
          </span>
        )}
      </div>
    );
  }, [entitlement]);

  return (
    <SummarySectionV2.SummaryGridCalloutItem testId="summary-pricing-plan">
      <div className="billing-plan billing-cycle title-height">
        <label>Pricing</label>
        {getPricingPlan()}
        {isViewPricingEnabled && (
          <ViewPricingDetails entitlement={entitlement} refetch={refetch} linkedEntitlements={linkedEntitlements} />
        )}
      </div>
    </SummarySectionV2.SummaryGridCalloutItem>
  );
};
